<template>
  <div v-if="fundingData && fundingData.length > 0" style="overflow: hidden; position: relative">
    <div class="section-header"></div>

    <div
      class="content-card"
      :style="{ overflow: 'auto', height: $vuetify.display.xs ? 'auto' : '360px', position: 'relative' }"
    >
      <div v-if="meta?.financials === 'Y'">
        <Chart :options="chartOptionsFundingStructure"></Chart>
      </div>

      <div v-else class="info-section">
        <h4 class="info-section-title">Debt Summary</h4>
        <div class="info-grid">
          <div v-for="[key, , value] in fundingData.filter((d) => typeof d[2] === 'number')" :key="key">
            <div class="text-sm uppercase text-[var(--p-text-muted-color)]">
              {{ key }}
            </div>

            <div class="text-xl font-['Roboto'] font-thin">{{ formatSummaryValue(value, key) }}</div>
          </div>
        </div>
      </div>
    </div>
    <span class="footnote">Currency:{{ meta?.currency }}; Units: {{ meta?.units }} when applicable</span>
  </div>
</template>

<script lang="ts">
type FundingData = [catagory: string, _: null, value: number][]
type MetaData = {
  financials: string
  currency: string
  units: string
}
</script>

<script setup lang="ts">
import Chart from "@wbim/highcharts-themed"
import { numberUtils } from "@wbim/utils"
import { computed, onMounted, ref } from "vue"

import EventBus from "@/utils/eventbus.js"

const fundingData = ref<FundingData>([])
const meta = ref<MetaData>()

onMounted(() => {
  EventBus.$on("reload-funding", (data: { fundingData: FundingData; meta: MetaData }) => {
    meta.value = data.meta
    fundingData.value = data.fundingData
  })
})

const total = computed(() => fundingData.value?.find((d) => (d[0] ?? "").trim() === "Total")?.[2] ?? 0)
const dataRows = computed(() => fundingData.value.filter((d) => d[0] !== "" && d[0].trim() !== "Total" && d[2] > 0))

const chartOptionsFundingStructure = computed<Highcharts.Options>(() => {
  return {
    chart: { type: "pie", height: 316, borderWidth: 0 },
    title: { text: "Funding Structure" },
    tooltip: { enabled: false },
    plotOptions: {
      pie: {
        dataLabels: {
          // alignTo: "plotEdges",
          enabled: true,
          useHTML: true,
          style: {
            fontWeight: "normal"
          },
          formatter() {
            const pct = numberUtils.formatPercent(this.y! / total.value)
            return `<div style="white-space:nowrap">${this.key}: ${pct}<br> <span style="font-size:16px">${this.y!.toFixed(0)} </span><div>`
          }
        }
      }
    },
    series: [
      {
        name: "Funding Structure",
        type: "pie",
        data: total.value > 0 ? dataRows.value.map((d) => ({ name: d[0], y: d[2] })) : []
      }
    ]
  }
})

function formatSummaryValue(value: number | null | undefined, key: string) {
  if (value === null || value === undefined) {
    return ""
  }
  const percentageKeys = ["DEBT TO BOOK CAPITALZATION", "DEBT TO EQUITY"]
  const keyUpper = key.toUpperCase().trim()
  if (percentageKeys.includes(keyUpper)) {
    return numberUtils.formatPercent(value)
  }
  return numberUtils.format(value, 0)
}
</script>

<style>
.info-section,
.info-section .text-overline {
  font-family: "Roboto Condensed" !important;
}
.info-section-title {
  margin: 8px 0;
  text-align: center;
  font-weight: normal;
  font-size: 18px;
}
.footnote {
  position: absolute;
  bottom: 4px;
  left: 16px;
  font-style: italic;
  font-size: 10px;
  font-weight: 400;
  color: #9e9e9e;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  grid-gap: 16px;
  grid-auto-flow: row;
  overflow: auto;
  position: relative;
}
</style>
